import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Target, Plus, Trash2, Edit, Loader2, MapPin, Clock, Users } from "lucide-react";

const daysOfWeek = [
  { value: "MON", label: "Monday" },
  { value: "TUE", label: "Tuesday" },
  { value: "WED", label: "Wednesday" },
  { value: "THU", label: "Thursday" },
  { value: "FRI", label: "Friday" },
  { value: "SAT", label: "Saturday" },
  { value: "SUN", label: "Sunday" },
];

const ageBands = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
const genderOptions = ["all", "male", "female", "other"];

export default function Targeting() {
  const { token } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    location_ids: [],
    radius_meters: 1000,
    suburbs: [],
    postcodes: [],
    days_of_week: ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"],
    time_start: "00:00",
    time_end: "23:59",
    age_bands: [],
    gender: ["all"],
    context_tags: []
  });
  const [saving, setSaving] = useState(false);
  const [suburbInput, setSuburbInput] = useState("");
  const [postcodeInput, setPostcodeInput] = useState("");
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [configsRes, locationsRes] = await Promise.all([
        axios.get(`${API}/targeting`, { headers }),
        axios.get(`${API}/locations`, { headers })
      ]);
      setConfigs(configsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const days = prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day];
      return { ...prev, days_of_week: days };
    });
  };

  const handleLocationToggle = (locationId) => {
    setFormData(prev => {
      const ids = prev.location_ids.includes(locationId)
        ? prev.location_ids.filter(id => id !== locationId)
        : [...prev.location_ids, locationId];
      return { ...prev, location_ids: ids };
    });
  };

  const handleAgeBandToggle = (band) => {
    setFormData(prev => {
      const bands = prev.age_bands.includes(band)
        ? prev.age_bands.filter(b => b !== band)
        : [...prev.age_bands, band];
      return { ...prev, age_bands: bands };
    });
  };

  const addSuburb = () => {
    if (suburbInput.trim() && !formData.suburbs.includes(suburbInput.trim())) {
      setFormData(prev => ({ ...prev, suburbs: [...prev.suburbs, suburbInput.trim()] }));
      setSuburbInput("");
    }
  };

  const removeSuburb = (suburb) => {
    setFormData(prev => ({ ...prev, suburbs: prev.suburbs.filter(s => s !== suburb) }));
  };

  const addPostcode = () => {
    if (postcodeInput.trim() && !formData.postcodes.includes(postcodeInput.trim())) {
      setFormData(prev => ({ ...prev, postcodes: [...prev.postcodes, postcodeInput.trim()] }));
      setPostcodeInput("");
    }
  };

  const removePostcode = (postcode) => {
    setFormData(prev => ({ ...prev, postcodes: prev.postcodes.filter(p => p !== postcode) }));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.context_tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, context_tags: [...prev.context_tags, tagInput.trim()] }));
      setTagInput("");
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, context_tags: prev.context_tags.filter(t => t !== tag) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingConfig) {
        await axios.put(`${API}/targeting/${editingConfig.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Targeting config updated");
      } else {
        await axios.post(`${API}/targeting`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Targeting config created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save targeting config");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this targeting config?")) return;
    try {
      await axios.delete(`${API}/targeting/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Targeting config deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete targeting config");
    }
  };

  const openEditDialog = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      location_ids: config.location_ids || [],
      radius_meters: config.radius_meters,
      suburbs: config.suburbs || [],
      postcodes: config.postcodes || [],
      days_of_week: config.days_of_week || [],
      time_start: config.time_start,
      time_end: config.time_end,
      age_bands: config.age_bands || [],
      gender: config.gender || ["all"],
      context_tags: config.context_tags || []
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingConfig(null);
    setFormData({
      name: "",
      location_ids: [],
      radius_meters: 1000,
      suburbs: [],
      postcodes: [],
      days_of_week: ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"],
      time_start: "00:00",
      time_end: "23:59",
      age_bands: [],
      gender: ["all"],
      context_tags: []
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading targeting configs...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="targeting-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Targeting</h1>
          <p className="text-muted-foreground mt-1">
            Configure targeting rules for your campaigns
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-targeting-btn">
              <Plus className="mr-2 h-4 w-4" />
              Create Targeting Config
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {editingConfig ? "Edit Targeting Config" : "Create Targeting Config"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Config Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="CBD Peak Hours"
                  required
                  data-testid="targeting-name-input"
                />
              </div>

              {/* Geo Targeting */}
              <div className="space-y-4 p-4 rounded-lg bg-secondary/20 border border-border">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold">Geo Targeting</h4>
                </div>

                {locations.length > 0 && (
                  <div className="space-y-2">
                    <Label>Linked Locations</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                      {locations.map(loc => (
                        <div
                          key={loc.id}
                          className={`p-2 rounded border cursor-pointer text-sm transition-colors ${
                            formData.location_ids.includes(loc.id)
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => handleLocationToggle(loc.id)}
                        >
                          {loc.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Radius (meters)</Label>
                  <Input
                    type="number"
                    min={100}
                    max={50000}
                    value={formData.radius_meters}
                    onChange={(e) => setFormData(prev => ({ ...prev, radius_meters: parseInt(e.target.value) || 1000 }))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Suburbs</Label>
                    <div className="flex gap-2">
                      <Input
                        value={suburbInput}
                        onChange={(e) => setSuburbInput(e.target.value)}
                        placeholder="Add suburb"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSuburb())}
                      />
                      <Button type="button" variant="outline" onClick={addSuburb}>Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {formData.suburbs.map(s => (
                        <Badge key={s} variant="secondary" className="cursor-pointer" onClick={() => removeSuburb(s)}>
                          {s} ×
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Postcodes</Label>
                    <div className="flex gap-2">
                      <Input
                        value={postcodeInput}
                        onChange={(e) => setPostcodeInput(e.target.value)}
                        placeholder="Add postcode"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPostcode())}
                      />
                      <Button type="button" variant="outline" onClick={addPostcode}>Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {formData.postcodes.map(p => (
                        <Badge key={p} variant="secondary" className="cursor-pointer" onClick={() => removePostcode(p)}>
                          {p} ×
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Time Targeting */}
              <div className="space-y-4 p-4 rounded-lg bg-secondary/20 border border-border">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold">Time Targeting</h4>
                </div>
                <div className="space-y-2">
                  <Label>Days of Week</Label>
                  <div className="flex flex-wrap gap-2">
                    {daysOfWeek.map(day => (
                      <Badge
                        key={day.value}
                        variant={formData.days_of_week.includes(day.value) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => handleDayToggle(day.value)}
                      >
                        {day.label.slice(0, 3)}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={formData.time_start}
                      onChange={(e) => setFormData(prev => ({ ...prev, time_start: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={formData.time_end}
                      onChange={(e) => setFormData(prev => ({ ...prev, time_end: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Demographics */}
              <div className="space-y-4 p-4 rounded-lg bg-secondary/20 border border-border">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold">Demographics</h4>
                </div>
                <div className="space-y-2">
                  <Label>Age Bands</Label>
                  <div className="flex flex-wrap gap-2">
                    {ageBands.map(band => (
                      <Badge
                        key={band}
                        variant={formData.age_bands.includes(band) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => handleAgeBandToggle(band)}
                      >
                        {band}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select
                    value={formData.gender[0] || "all"}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, gender: [value] }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {genderOptions.map(g => (
                        <SelectItem key={g} value={g} className="capitalize">{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Context Tags */}
              <div className="space-y-2">
                <Label>Context Tags</Label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="e.g. nightlife, business, airport"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {formData.context_tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-targeting-btn">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {editingConfig ? "Update Config" : "Create Config"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Configs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {configs.map(config => (
          <Card key={config.id} className="bg-card border-border hover:border-primary/50 transition-colors" data-testid={`targeting-card-${config.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Target className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{config.name}</h3>
                    <p className="text-sm text-muted-foreground">{config.radius_meters}m radius</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{config.time_start} - {config.time_end}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {config.days_of_week?.slice(0, 4).map(d => (
                    <Badge key={d} variant="outline" className="text-xs">{d}</Badge>
                  ))}
                  {config.days_of_week?.length > 4 && (
                    <Badge variant="outline" className="text-xs">+{config.days_of_week.length - 4}</Badge>
                  )}
                </div>
                {config.suburbs?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {config.suburbs.slice(0, 2).map(s => (
                      <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                    ))}
                    {config.suburbs.length > 2 && (
                      <Badge variant="secondary" className="text-xs">+{config.suburbs.length - 2}</Badge>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEditDialog(config)}>
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDelete(config.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {configs.length === 0 && (
        <Card className="bg-card border-border border-dashed">
          <CardContent className="p-12 text-center">
            <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">No targeting configs yet</h3>
            <p className="text-muted-foreground mb-4">Create targeting rules to refine your ad delivery</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Create Config
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
